import type { DocNode } from '@atlaskit/adf-schema';

import { htmlToADF } from '@atlassian/html-adf-transformer';
import type { ContentType as AiIssueCreateContentType } from '@atlassian/ai-issue-create';

type ContentType = AiIssueCreateContentType | 'folder';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import {
	type IssueCreateSidePanelActions,
	type PageMode,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { openSingleIssueSidePanelWithSelection } from '@confluence/issue-create-side-panel/entry-points/singleCreateUtils.ts';
import { getClosestActionListFromSelection } from '@confluence/issue-create-side-panel/entry-points/actionItemUtils.ts';
import { getClosestTableFromSelection } from '@confluence/issue-create-side-panel/entry-points/tableUtils.ts';
import {
	constructInsertMultipleStorageFragmentsRequestPayload,
	constructInsertStorageColumnTableRequestPayload,
	getValidRange,
	getValidRangeForActionItem,
} from '@confluence/issue-create-side-panel/entry-points/issueInsertionUtils.ts';
import {
	prepareBulkCreateFromSelection,
	BulkSelectionResult,
} from '@confluence/issue-create-side-panel/entry-points/bulkCreateSelectionUtils.ts';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';
import type { RightSidebarContextType } from '@confluence/page-layout-context';

import { convertSelectionToLegacyFormat } from './convertSelectionToLegacyFormat.ts';

type OptionalSelectionOrRange = Selection | Range | null | undefined;

export const getHTMLString = (activeSelection: OptionalSelectionOrRange): string => {
	if (!activeSelection || ('rangeCount' in activeSelection && activeSelection.rangeCount === 0)) {
		return '';
	}

	const range = 'getRangeAt' in activeSelection ? activeSelection.getRangeAt(0) : activeSelection;

	const docFragment = range.cloneContents();
	const tempDiv = document.createElement('div');
	tempDiv.appendChild(docFragment);

	return tempDiv.innerHTML;
};

export const SUMMARY_MAX_LENGTH = 254;

export const splitText = (text: string, maxLength: number): [string, string] => {
	const horizontalEllipsis = '…';
	if (text.length <= maxLength) {
		return [text, ''];
	}
	// Calculate the length of the first part without including the ellipsis
	const firstPartLength = maxLength - horizontalEllipsis.length;
	// Split the text into two parts
	const firstPart = text.substring(0, firstPartLength);
	const firstPartWithHorizontalEllipis = firstPart + horizontalEllipsis;
	return [firstPartWithHorizontalEllipis, text];
};

export const getSummaryAndDescription = (
	activeSelection: OptionalSelectionOrRange,
): [string, DocNode] => {
	const selectionString = activeSelection?.toString() || '';
	const htmlString = getHTMLString(activeSelection);

	const [summary] = splitText(selectionString, SUMMARY_MAX_LENGTH);
	/*
	 The regex matches one or more occurrences of any line break (\r, \n) or tab (\t)
	 and replaces them all with a single space. (We only consider whitespace characters because
	 these are the only ones that ultimately stick when parsing the summary.)
  */
	const summaryWithoutFormatting = summary.replace(/[\r\n\t]+/g, ' ');

	// Convert the full HTML content to ADF for the description
	const descriptionAdf = htmlToADF(htmlString);

	return [summaryWithoutFormatting, descriptionAdf];
};

export interface CreateSingleJiraIssueHandleParams {
	rightSidebarContext: RightSidebarContextType;
	actions: IssueCreateSidePanelActions;
	activeSelection?: Selection | null;
	contentId: string;
	contentType?: ContentType;
	container: HTMLElement;
	lastModified?: string | null;
	isCreatedWithAi?: boolean;
	hasEditPermission: boolean;
	pageMode?: PageMode;
	isActionItemInSelection?: boolean;
}

export interface CreateSingleAiJiraIssueHandleParams {
	rightSidebarContext: RightSidebarContextType;
	actions: IssueCreateSidePanelActions;
	activeSelection?: Selection | null;
	contentId: string;
	contentType?: ContentType;
	container: HTMLElement;
	fireAiAnalytics: () => void;
	lastModified?: string | null;
	pressTracing?: (timeStamp?: number) => void;
	hasEditPermission: boolean;
	pageMode?: PageMode;
	isActionItemInSelection?: boolean;
}

export interface CreateBulkJiraIssuesHandleParams {
	rightSidebarContext: RightSidebarContextType;
	actions: IssueCreateSidePanelActions;
	setIsCreatedWithAi: (isCreatedWithAi: boolean) => void;
	setIsCreatedFromTable: (isCreatedFromTable: boolean) => void;
	setIsCreatedFromActionItems: (isCreatedFromActionItems: boolean) => void;
	pressTracing?: (timeStamp?: number) => void;
	activeSelection?: Selection | null;
	contentId: string;
	container: HTMLElement;
	lastModified?: string | null;
	hasEditPermission: boolean;
	pageMode?: PageMode;
}

export const createSingleJiraIssueHandle = ({
	rightSidebarContext,
	actions,
	activeSelection,
	contentId,
	contentType,
	container,
	lastModified,
	hasEditPermission,
	pageMode,
	isActionItemInSelection,
}: CreateSingleJiraIssueHandleParams) => {
	void actions.setIsCreatedWithAi(false);
	void actions.setHasEditPermission(hasEditPermission);
	openSingleIssueSidePanelWithSelection({
		actions,
		rightSidebarContext,
		activeSelection,
		contentId,
		contentType,
		container,
		lastModified,
		pageMode,
		isActionItemInSelection,
		convertSelectionToLegacyFormat,
	});
};

export const createSingleAiJiraIssueHandle = ({
	rightSidebarContext,
	actions,
	activeSelection,
	contentId,
	contentType,
	container,
	fireAiAnalytics,
	lastModified,
	pressTracing,
	hasEditPermission,
	pageMode,
	isActionItemInSelection,
}: CreateSingleAiJiraIssueHandleParams) => {
	fireAiAnalytics();
	void actions.setIsCreatedWithAi(true);
	void actions.setHasEditPermission(hasEditPermission);
	openSingleIssueSidePanelWithSelection({
		actions,
		rightSidebarContext,
		activeSelection,
		contentId,
		contentType,
		container,
		lastModified,
		pageMode,
		isActionItemInSelection,
		convertSelectionToLegacyFormat,
	});
	pressTracing?.();
};

export const createMultipleJiraIssuesHandleModernized = ({
	actions,
	setIsCreatedWithAi,
	setIsCreatedFromTable,
	setIsCreatedFromActionItems,
	rightSidebarContext,
	pressTracing,
	activeSelection,
	contentId,
	container,
	lastModified,
	hasEditPermission,
	pageMode,
}: CreateBulkJiraIssuesHandleParams) => {
	if (expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
		const result = prepareBulkCreateFromSelection({
			actions,
			activeSelection,
			contentId,
			pageMode,
			hasEditPermission,
			container,
			lastModified,
			setIsCreatedFromTable,
			setIsCreatedFromActionItems,
			convertSelectionToLegacyFormat,
		});
		if (result === BulkSelectionResult.None) {
			return;
		}
	} else {
		const selectedActionList = getClosestActionListFromSelection(activeSelection, pageMode);
		const selectedTable = getClosestTableFromSelection(activeSelection);
		if (
			!selectedTable &&
			!selectedActionList &&
			fg('confluence-create-work-items-from-action-items')
		) {
			// We have checks to ensure that the activeSelection is in a table or contains action items, so this should never happen.
			return;
		}
		if (selectedTable) {
			void actions.setSelectedTable(selectedTable);
			const insertStorageColumnTableRequestPayload =
				constructInsertStorageColumnTableRequestPayload(
					contentId,
					convertSelectionToLegacyFormat,
					getValidRange(activeSelection?.getRangeAt(0)),
					container,
					lastModified,
				);
			void actions.setInsertStorageColumnTableRequestPayload(
				insertStorageColumnTableRequestPayload,
			);
			setIsCreatedFromTable(true);
			resetActionItemsState(setIsCreatedFromActionItems, actions);
		} else if (selectedActionList && fg('confluence-create-work-items-from-action-items')) {
			void actions.setSelectedActionItems(selectedActionList as HTMLElement);
			const actionItems = selectedActionList.querySelectorAll(
				'div[data-task-local-id]:not([data-task-local-id=""])',
			);
			const ranges: Range[] = [];
			actionItems.forEach((actionItem) => {
				const range = new Range();
				range.selectNodeContents(actionItem);
				const validRange = getValidRangeForActionItem(range);
				if (validRange) {
					ranges.push(validRange);
				}
			});
			const insertMultipleStorageFragmentsRequestPayload =
				constructInsertMultipleStorageFragmentsRequestPayload(
					contentId,
					convertSelectionToLegacyFormat,
					ranges,
					container,
					lastModified,
				);
			void actions.setInsertMultipleStorageFragmentsRequestPayload(
				insertMultipleStorageFragmentsRequestPayload,
			);
			setIsCreatedFromActionItems(true);
			resetTableState(setIsCreatedFromTable, actions);
		}
		void actions.setContentId(contentId);
		void actions.setPageMode(pageMode);
		void actions.setHasEditPermission(hasEditPermission);
		setIsCreatedWithAi(false);
	}
	rightSidebarContext.open(BULK_ISSUE_CREATE_SIDE_PANEL_ID, BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH);
	pressTracing?.();
};

const resetTableState = (
	setIsCreatedFromTable: (isCreatedFromTable: boolean) => void,
	actions: IssueCreateSidePanelActions,
) => {
	setIsCreatedFromTable(false);
	void actions.setSelectedTable(undefined);
	void actions.setInsertStorageColumnTableRequestPayload(undefined);
};

const resetActionItemsState = (
	setIsCreatedFromActionItems: (isCreatedFromActionItems: boolean) => void,
	actions: IssueCreateSidePanelActions,
) => {
	setIsCreatedFromActionItems(false);
	void actions.setSelectedActionItems(undefined);
	void actions.setInsertMultipleStorageFragmentsRequestPayload(undefined);
};
